import { Image as AntImage, Button, Input, Table, Tag, Tooltip } from "antd";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import Plus from "../../assets/images/plus.svg";
import ConfirmationBox from "../../components/ConfirmationBox";
import DeleteModal from "../../components/DeleteModal";
import SectionWrapper from "../../components/SectionWrapper";
import apiPath from "../../constants/apiPath";
import { AppStateContext } from "../../context/AppContext";
import lang from "../../helper/langHelper";
import { Severty, ShowToast } from "../../helper/toast";
import useDebounce from "../../hooks/useDebounce";
import useRequest from "../../hooks/useRequest";

import AddForm from "./AddForm";

function Index() {
  const heading = lang("Staff") + " " + lang("Management");
  const { setPageHeading } = useContext(AppStateContext);

  const sectionName = "Add Staff";
  const { request } = useRequest();
  const navigate = useNavigate();

  const api = {
    list: apiPath.staffList,
    addEdit: apiPath.addEditStaff,
    delete: apiPath.staffDelete,
    statusChange: apiPath.staffStatusChange,
  };

  const [searchText, setSearchText] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [visible, setVisible] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const debouncedSearchText = useDebounce(searchText, 300);
  const { showConfirm } = ConfirmationBox();

  const columns = [
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      render: (_, { image }) => (
        <AntImage
          src={image}
          alt="service"
          width={80}
          height={60}
          style={{
            objectFit: "cover",
            borderRadius: 6,
            border: "1px solid #eee",
          }}
          preview={{ mask: "Preview" }}
        />
      ),
    },
    {
      title: "Service Name",
      dataIndex: "name",
      key: "name",
      render: (text) => text || "-",
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (val) => (val ? `$${val}` : "-"),
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      render: (val) => (val ? `${val} min` : "-"),
    },
    {
      title: "Rooms",
      dataIndex: "rooms",
      key: "rooms",
      render: (rooms) =>
        Array.isArray(rooms) && rooms.length > 0 ? rooms.join(", ") : "-",
    },
    {
      title: "Chairs",
      dataIndex: "chairs",
      key: "chairs",
      render: (chairs) =>
        Array.isArray(chairs) && chairs.length > 0 ? chairs.join(", ") : "-",
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      filters: [
        { text: "Active", value: true },
        { text: "Inactive", value: false },
      ],
      render: (_, { is_active, _id }) => (
        <Tag
          color={is_active ? "green" : "red"}
          onClick={() =>
            showConfirm({
              record: _id,
              path: api.statusChange,
              onLoading: () => setLoading(true),
              onSuccess: () => {
                setRefresh((prev) => !prev);
                ShowToast(
                  `Status changed to ${
                    !is_active ? "Active" : "Inactive"
                  } successfully`,
                  Severty.SUCCESS
                );
              },
            })
          }
          style={{ cursor: "pointer" }}
        >
          {is_active ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      render: (_, record) => (
        <div className="d-flex gap-2">
          <Tooltip title="Edit" color="purple">
            <Button
              className="btnStyle primary_btn"
              onClick={() => {
                setSelected(record);
                setVisible(true);
              }}
            >
              <i className="fas fa-edit"></i>
            </Button>
          </Tooltip>
          <Tooltip title="Delete" color="purple">
            <Button
              className="btnStyle deleteDangerbtn"
              onClick={() => {
                setSelected(record);
                setShowDelete(true);
              }}
            >
              <i className="fas fa-trash"></i>
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  const fetchData = (pagination, filters) => {
    const filterActive = filters ? filters.is_active : null;

    request({
      url:
        api.list +
        `?status=${filterActive ? filterActive.join(",") : ""}&page=${
          pagination.current
        }&pageSize=${pagination.pageSize}&search=${debouncedSearchText}`,
      method: "GET",
      onSuccess: ({ data }) => {
        setList(data?.list?.docs || []);
        setPagination({
          current: data?.list?.page,
          total: data?.list?.totalDocs,
          pageSize: data?.list?.limit,
        });
        setLoading(false);
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  useEffect(() => {
    setPageHeading(heading);
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchData({ ...pagination, current: 1 });
  }, [refresh, debouncedSearchText]);

  const handleChange = (pagination, filters) => {
    fetchData(pagination, filters);
  };

  return (
    <>
      <SectionWrapper
        cardHeading={lang("Staff") + " " + lang("List")}
        extra={
          <div className="d-flex align-items-center gap-3 text-head_right_cont">
            <div className="pageHeadingSearch d-flex gap-2">
              <Input.Search
                className="searchInput"
                placeholder="Search by Staff Name"
                allowClear
                onChange={(e) => setSearchText(e.target.value.trimStart())}
              />
              <Button
                className="primary_btn btnStyle"
                onClick={() => {
                  setVisible(true);
                  setSearchText("");
                }}
              >
                <span className="add-Ic">
                  <img src={Plus} alt="add" />
                </span>
                {sectionName}
              </Button>
            </div>
          </div>
        }
      >
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={list}
          loading={loading}
          pagination={pagination}
          onChange={handleChange}
        />
      </SectionWrapper>

      {visible && (
        <AddForm
          section={sectionName}
          api={api}
          show={visible}
          hide={() => {
            setVisible(false);
            setSelected(null);
          }}
          data={selected}
          refresh={() => setRefresh((prev) => !prev)}
        />
      )}

      {showDelete && (
        <DeleteModal
          title={"Delete Staff"}
          subtitle={`Are you sure you want to delete this staff?`}
          show={showDelete}
          hide={() => {
            setShowDelete(false);
            setSelected(null);
          }}
          onOk={() => {
            request({
              url: `${api.delete}/${selected?._id}`,
              method: "POST",
              onSuccess: () => {
                ShowToast("Staff deleted successfully", Severty.SUCCESS);
                setRefresh((prev) => !prev);
                setShowDelete(false);
              },
              onError: (err) => ShowToast(err, Severty.ERROR),
            });
          }}
        />
      )}
    </>
  );
}

export default Index;
